import React, { useState } from 'react';
import { Grid, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'services/localization/useTranslation';

interface OwnProps {
	handleInputChange: (valueInMinutes: number) => void;
	timePeriodInMinutes: number;
	disabled?: boolean;
}

type Props = OwnProps;

const TimePeriodInput = (props: Props) => {
	const { timePeriodInMinutes, handleInputChange } = props;

	const getTimePeriodWithCorrectUnit = (timePeriodInMinutes: number) => {
		const timePeriodInHours = timePeriodInMinutes / 60;
		const timePeriodInDays = timePeriodInHours / 24;
		if (timePeriodInDays >= 1 && timePeriodInHours % 24 === 0) {
			return { value: timePeriodInDays, unit: 'days' };
		} else if (timePeriodInHours >= 1 && timePeriodInMinutes % 60 === 0) {
			return { value: timePeriodInHours, unit: 'hours' };
		} else {
			return { value: timePeriodInMinutes, unit: 'minutes' };
		}
	};
	const { t } = useTranslation();

	const { value, unit } = getTimePeriodWithCorrectUnit(timePeriodInMinutes);
	const [timePeriodValue, setTimePeriodValue] = useState<number | ''>(value);
	const [timePeriodUnit, setTimePeriodUnit] = useState<string>(unit);
	const timePeriodUnits = ['minutes', 'hours', 'days'];

	const onValueOrUnitChange = (newValue: string, newUnit: string) => {
		const roundedValue = Math.round(parseFloat(newValue));
		const valueWithoutNan = isNaN(roundedValue) ? '' : roundedValue;
		setTimePeriodUnit(newUnit);
		setTimePeriodValue(valueWithoutNan);
		const valueInMinutes = calculateValueInMinutes(valueWithoutNan, newUnit);
		handleInputChange(valueInMinutes);
	};

	const calculateValueInMinutes = (newValue: number | '', newUnit: string) => {
		switch (newUnit) {
			case 'minutes':
				return Number(newValue);
			case 'hours':
				return Number(newValue) * 60;
			case 'days':
				return Number(newValue) * 24 * 60;
			default:
				return Number(newValue);
		}
	};

	const getUnitInCorrectFormatForDisplay = (unit: string, value: number | '') => {
		if (value !== 1) {
			switch (unit) {
				case 'minutes':
					return t('common:times.minutes', 'minutes');
				case 'hours':
					return t('common:times.hours', 'hours');
				case 'days':
					return t('common:times.days', 'days');
				default:
					return unit;
			}
		}

		switch (unit) {
			case 'minutes':
				return t('common:times.min', 'min');
			case 'hours':
				return t('common:times.hour');
			case 'days':
				return t('common:times.dayUntil', 'day (until shop closing)');
			default:
				return unit;
		}
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={3} sm={1}>
				<TextField
					name="maintenance time"
					variant="standard"
					fullWidth
					value={timePeriodValue}
					onChange={(e) => onValueOrUnitChange(e.target.value, timePeriodUnit)}
					margin="normal"
					disabled={props.disabled}
				/>
			</Grid>
			<Grid item xs={7} sm={3}>
				<TextField
					select
					name="timeValue"
					variant="standard"
					fullWidth
					value={timePeriodUnit}
					onChange={(e) => onValueOrUnitChange(timePeriodValue.toString(), e.target.value)}
					margin="normal"
					type="number"
					disabled={props.disabled}
				>
					{timePeriodUnits.map((timePeriodUnit) => (
						<MenuItem key={timePeriodUnit} value={timePeriodUnit}>
							{getUnitInCorrectFormatForDisplay(timePeriodUnit, timePeriodValue)}
						</MenuItem>
					))}
				</TextField>
			</Grid>
		</Grid>
	);
};

export default TimePeriodInput;
